import { useEffect } from 'react';

import { usePersistentState } from '@cast/utils';
import { useReportingTrial, useTrial } from '@cast/shared-trial-ui';

import { useTrialHasEndedDialog } from '../../components/trial/dialogs/useTrialHasEndedDialog';
import { useTrialInProgressDialog } from '../../components/trial/dialogs/useTrialInProgressDialog';
import { useIsOrganizationReadOnly } from '../../hooks/useIsOrganizationReadOnly';

export const TrialNudgeUserManager = () => {
  const accountReadOnly = useIsOrganizationReadOnly();
  const [trialNudge15Days, setTrialNudge15Days] = usePersistentState<boolean>(
    'PLG-nudge-15-days',
    false
  );
  const [trialNudge7Days, setTrialNudge7Days] = usePersistentState<boolean>(
    'PLG-nudge-7-days',
    false
  );
  const [trialNudge3Days, setTrialNudge3Days] = usePersistentState<boolean>(
    'PLG-nudge-3-days',
    false
  );
  const [trialNudgeExpired, setTrialNudgeExpired] = usePersistentState<boolean>(
    'PLG-nudge-expired',
    false
  );

  const { openDialog: openTrialInProgressDialog } = useTrialInProgressDialog();
  const { openDialog: openTrialHasEndedDialog } = useTrialHasEndedDialog();
  const { isEligible, state, expiresInDays } = useTrial();
  const {
    isEligible: isReportingEligible,
    isLoading: isReportingTrialLoading,
  } = useReportingTrial();

  useEffect(() => {
    // Wait for reporting eligibility to resolve before evaluating PLG nudges.
    if (isReportingTrialLoading) {
      return;
    }

    // Reporting monetization uses its own Status / explain dialog — skip PLG nudges.
    if (isReportingEligible) {
      return;
    }

    if (
      !accountReadOnly &&
      isEligible &&
      state === 'post-trial' &&
      !trialNudgeExpired
    ) {
      const markNudgeAsSeen = () => {
        setTrialNudgeExpired(true);
        setTrialNudge3Days(true);
        setTrialNudge7Days(true);
        setTrialNudge15Days(true);
      };

      openTrialHasEndedDialog({
        onClose: markNudgeAsSeen,
      });
    }

    if (!accountReadOnly && isEligible && state === 'active') {
      if (expiresInDays <= 3 && !trialNudge3Days) {
        const markNudgeAsSeen = () => {
          setTrialNudge3Days(true);
          setTrialNudge7Days(true);
          setTrialNudge15Days(true);
        };

        openTrialInProgressDialog({
          onClose: markNudgeAsSeen,
          onSettle: markNudgeAsSeen,
        });

        return;
      }

      if (expiresInDays <= 7 && !trialNudge7Days) {
        const markNudgeAsSeen = () => {
          setTrialNudge7Days(true);
          setTrialNudge15Days(true);
        };

        openTrialInProgressDialog({
          onClose: markNudgeAsSeen,
          onSettle: markNudgeAsSeen,
        });

        return;
      }

      if (expiresInDays <= 15 && !trialNudge15Days) {
        const markNudgeAsSeen = () => {
          setTrialNudge15Days(true);
        };

        openTrialInProgressDialog({
          onClose: markNudgeAsSeen,
          onSettle: markNudgeAsSeen,
        });

        return;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReportingTrialLoading, isReportingEligible]);

  return null;
};
